import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Plus, Save, Eye, Upload, FileText, Video, Image, Link, Trash2, Edit, CheckCircle, AlertCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { authUploadUrl, api, API_BASE_URL } from '../../api'; // Centralized API client

interface Category {
  _id: string;
  name: string;
  slug: string;
  description: string;
}

interface Module {
  _id?: string;
  title: string;
  description: string;
  type: 'video' | 'pdf' | 'text' | 'image' | 'document' | 'link';
  content?: string; // For text content or external_link
  fileUrl?: string; // For uploaded files (from backend)
  externalUrl?: string; // For link type (from backend)
  file?: File; // For new file uploads (frontend only)
  order?: number;
  duration: number;
  isRequired: boolean;
}

interface Section {
  _id?: string;
  title: string;
  description: string;
  order?: number;
  modules: Module[];
}

interface CourseForm {
  title: string;
  description: string;
  shortDescription: string;
  category: string;
  price: number;
  originalPrice?: number;
  level: 'beginner' | 'intermediate' | 'advanced';
  tags: string; // Will be comma-separated string
  requirements: string; // Will be newline-separated string
  objectives: string; // Will be newline-separated string
  targetAudience: string; // Will be newline-separated string
  settings: {
    allowDownload: boolean;
    allowComments: boolean;
    sequentialAccess: boolean;
    timeLimit: number;
  };
  certificate: {
    enabled: boolean;
    passingScore: number;
  };
  estimatedDuration: number;
  isPublished: boolean;
  status: 'draft' | 'pending' | 'published' | 'archived';
  sections: Section[]; // Added sections to the form data
}

const CourseCreator: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [course, setCourse] = useState<any>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeTab, setActiveTab] = useState<'info' | 'content' | 'settings'>('info');
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [editingModule, setEditingModule] = useState<{ sectionId: string; module: Module | null }>({ sectionId: '', module: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);


  const { register, handleSubmit, setValue, watch, control, formState: { errors } } = useForm<CourseForm>({
    defaultValues: {
      title: '',
      description: '',
      shortDescription: '',
      category: '',
      price: 0,
      originalPrice: undefined,
      level: 'beginner',
      tags: '',
      requirements: '',
      objectives: '',
      targetAudience: '',
      settings: {
        allowDownload: false,
        allowComments: false,
        sequentialAccess: false,
        timeLimit: 0
      },
      certificate: {
        enabled: false,
        passingScore: 70
      },
      estimatedDuration: 0,
      isPublished: false,
      status: 'draft',
      sections: [] // Initialize sections as an empty array
    }
  });

  // Watch sections from form state
  const sections = watch('sections');

  const levels = [
    { value: 'beginner', label: 'Débutant' },
    { value: 'intermediate', label: 'Intermédiaire' },
    { value: 'advanced', label: 'Avancé' },
  ];

  const statusOptions = [
    { value: 'draft', label: 'Brouillon' },
    { value: 'pending', label: 'En attente' },
    { value: 'published', label: 'Publié' },
    { value: 'archived', label: 'Archivé' }
  ];

  const moduleTypes = [
    { value: 'video', label: 'Vidéo', icon: Video, color: 'text-red-600' },
    { value: 'pdf', label: 'PDF', icon: FileText, color: 'text-[#FFB639]' },
    { value: 'text', label: 'Texte', icon: FileText, color: 'text-gray-600' },
    { value: 'link', label: 'Lien externe', icon: Link, color: 'text-[#FFB639]' },
    { value: 'image', label: 'Image', icon: Image, color: 'text-[#FFB639]' },
    { value: 'document', label: 'Document', icon: FileText, color: 'text-[#FFB639]' }
  ];

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      loadCourse();
    } else {
      setLoading(false);
    }
  }, [id, isEditing]);

  const fetchCategories = async () => {
    try {
      const response = await api.get<Category[]>('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Erreur lors du chargement des catégories');
    }
  };

  const loadCourse = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/courses/${id}`);
      const courseData = response.data.data;
      setCourse(courseData);
      
      // Fill form fields
      setValue('title', courseData.title || '');
      setValue('description', courseData.description || '');
      setValue('shortDescription', courseData.shortDescription || '');
      setValue('category', courseData.category?._id || '');
      setValue('price', courseData.price || 0);
      setValue('originalPrice', courseData.originalPrice || undefined);
      setValue('level', courseData.level || 'beginner');
      setValue('tags', courseData.tags?.join(',') || '');
      setValue('requirements', courseData.requirements?.join('\n') || '');
      setValue('objectives', courseData.objectives?.join('\n') || '');
      setValue('targetAudience', courseData.targetAudience?.join('\n') || '');
      setValue('settings', courseData.settings || {
        allowDownload: false,
        allowComments: false,
        sequentialAccess: false,
        timeLimit: 0
      });
      setValue('certificate', courseData.certificate || {
        enabled: false,
        passingScore: 70
      });
      setValue('estimatedDuration', courseData.estimatedDuration || 0);
      setValue('isPublished', courseData.isPublished || false);
      setValue('status', courseData.status || 'draft');
      setValue('sections', courseData.sections || []); // Set sections from loaded data

      if (courseData.thumbnail) {
        setThumbnailPreview(authUploadUrl(courseData.thumbnail.startsWith('/uploads/')
          ? courseData.thumbnail
          : `/uploads${courseData.thumbnail}`));
      }
    } catch (error: any) {
      console.error('Load course error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors du chargement du cours');
      navigate('/admin/courses'); // Redirect if course not found or access denied
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: CourseForm) => {
    try {
      setSaving(true);
      const formData = new FormData();
      
      // Append course ID for update
      if (isEditing) {
        formData.append('id', id as string);
      }

      // Basic fields
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('shortDescription', data.shortDescription);
      formData.append('category', data.category);
      formData.append('price', data.price.toString());
      if (data.originalPrice) formData.append('originalPrice', data.originalPrice.toString());
      formData.append('level', data.level);
      formData.append('estimatedDuration', data.estimatedDuration.toString());
      formData.append('isPublished', String(data.isPublished));
      formData.append('status', data.status);

      // Arrays (split by comma for tags, newline for others)
      formData.append('tags', data.tags.split(',').map(s => s.trim()).filter(Boolean).join(','));
      formData.append('requirements', data.requirements.split('\n').map(s => s.trim()).filter(Boolean).join('\n'));
      formData.append('objectives', data.objectives.split('\n').map(s => s.trim()).filter(Boolean).join('\n'));
      formData.append('targetAudience', data.targetAudience.split('\n').map(s => s.trim()).filter(Boolean).join('\n'));

      // Objects (stringify)
      formData.append('settings', JSON.stringify(data.settings));
      formData.append('certificate', JSON.stringify(data.certificate));

      // Thumbnail
      if (thumbnail) {
        formData.append('thumbnail', thumbnail);
      }

      // Sections and Modules
      const courseSectionsToSubmit = data.sections.map((section, sectionIndex) => {
        const modulesToSubmit = section.modules.map((module, moduleIndex) => {
          if (module.file) {
            // If there's a new file, append it to FormData and reference it
            const fileKey = `module_file_${sectionIndex}_${moduleIndex}`;
            formData.append(fileKey, module.file);
            return { ...module, file: undefined, fileKey: fileKey }; // Remove file from module object, add fileKey
          }
          return module;
        });
        return { ...section, modules: modulesToSubmit };
      });
      formData.append('sections', JSON.stringify(courseSectionsToSubmit));


      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent: any) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      };

      const url = '/courses'; // Always POST to /courses, backend handles update based on 'id' in formData
      const response = await api.post(url, formData, config);

      if (response.status === 200 || response.status === 201) {
        const result = response.data;
        toast.success(isEditing ? 'Cours mis à jour' : 'Cours créé avec succès');
        
        setCourse(result.data);
        setValue('sections', result.data.sections || []); // Update sections with data from backend
        
        if (!isEditing) {
          navigate(`/admin/courses/${result.data._id}/edit`);
          setActiveTab('content'); // Switch to content tab after initial creation
        } else if (result.data.thumbnail) {
          setThumbnailPreview(`${API_BASE_URL}${result.data.thumbnail}`);
        }
      }
    } catch (error: any) {
      console.error('Save course error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expirée, veuillez vous reconnecter');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde');
      }
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  };

  // Section management (local state only)
  const addSection = (sectionData: { title: string; description: string }) => {
    const newSection: Section = {
      title: sectionData.title,
      description: sectionData.description,
      order: sections.length,
      modules: []
    };
    setValue('sections', [...sections, newSection]);
    setShowSectionModal(false);
    toast.success('Section ajoutée localement');
  };

  const updateSection = (sectionData: { title: string; description: string }) => {
    if (!editingSection) return;

    setValue('sections', sections.map(s => 
      s._id === editingSection._id || (s.title === editingSection.title && s.description === editingSection.description) // Fallback for new sections without _id
        ? { ...s, ...sectionData } 
        : s
    ));
    setShowSectionModal(false);
    setEditingSection(null);
    toast.success('Section mise à jour localement');
  };

  const deleteSection = (sectionId?: string, sectionTitle?: string) => {
    if (!confirm('Supprimer cette section et tous ses modules ?')) return;

    setValue('sections', sections.filter(s => 
      sectionId ? s._id !== sectionId : !(s.title === sectionTitle && s.description === editingSection?.description) // Fallback for new sections without _id
    ));
    toast.success('Section supprimée localement');
  };

  // Module management (local state only)
  const addModule = (sectionId: string, moduleData: Module) => {
    setValue('sections', sections.map(sec => 
      sec._id === sectionId || (!sec._id && sec.title === editingSection?.title) // Match by _id or by title for new sections
        ? { ...sec, modules: [...sec.modules, { ...moduleData, order: sec.modules.length }] } 
        : sec
    ));
    setShowModuleModal(false);
    toast.success('Module ajouté localement');
  };

  const updateModule = (sectionId: string, moduleData: Module) => {
    if (!editingModule.module) return;

    setValue('sections', sections.map(sec => 
      sec._id === sectionId || (!sec._id && sec.title === editingSection?.title)
        ? { 
            ...sec, 
            modules: sec.modules.map(mod => 
              mod._id === editingModule.module?._id || (!mod._id && mod.title === editingModule.module?.title)
                ? { ...mod, ...moduleData } 
                : mod
            )
          } 
        : sec
    ));
    setShowModuleModal(false);
    setEditingModule({ sectionId: '', module: null });
    toast.success('Module mis à jour localement');
  };

  const deleteModule = (sectionId: string, moduleId?: string, moduleTitle?: string) => {
    if (!confirm('Supprimer ce module ?')) return;

    setValue('sections', sections.map(sec => 
      sec._id === sectionId || (!sec._id && sec.title === editingSection?.title)
        ? { 
            ...sec, 
            modules: sec.modules.filter(mod => 
              moduleId ? mod._id !== moduleId : !(mod.title === moduleTitle && mod.description === editingModule.module?.description)
            ) 
          } 
        : sec
    ));
    toast.success('Module supprimé localement');
  };

  const getModuleIcon = (type: string) => moduleTypes.find(mt => mt.value === type)?.icon || FileText;
  const getModuleColor = (type: string) => moduleTypes.find(mt => mt.value === type)?.color || 'text-gray-600';

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setThumbnail(acceptedFiles[0]);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    multiple: false
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#FFB639]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Modifier le cours' : 'Créer un nouveau cours'}
          </h1>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/admin/courses')}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit(onSubmit)}
              disabled={saving}
              className="flex items-center px-6 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'info', name: 'Informations générales' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-[#FFB639] text-[#FFB639]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {activeTab === 'info' && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre du cours *
                  </label>
                  <input
                    {...register('title', { required: 'Le titre est requis' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    placeholder="Ex: Expert gestion GPS et analyse des données"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description complète *
                  </label>
                  <textarea
                    {...register('description', { required: 'La description est requise' })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    placeholder="Décrivez en détail le contenu et les objectifs de votre cours..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description courte *
                  </label>
                  <input
                    {...register('shortDescription', { 
                      required: 'La description courte est requise',
                      maxLength: {
                        value: 150,
                        message: 'La description courte ne doit pas dépasser 150 caractères'
                      }
                    })}
                    maxLength={150}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    placeholder="Résumé en une phrase (max 150 caractères)"
                  />
                  {errors.shortDescription && (
                    <p className="mt-1 text-sm text-red-600">{errors.shortDescription.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie *
                  </label>
                  <select
                    {...register('category', { required: 'La catégorie est requise' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Niveau *
                  </label>
                  <select
                    {...register('level', { required: 'Le niveau est requis' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  >
                    <option value="">Sélectionner un niveau</option>
                    {levels.map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                  {errors.level && (
                    <p className="mt-1 text-sm text-red-600">{errors.level.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prix (€) *
                  </label>
                  <input
                    {...register('price', { 
                      required: 'Le prix est requis', 
                      min: { value: 0, message: 'Le prix doit être positif' },
                      valueAsNumber: true
                    })}
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    placeholder="299.00"
                  />
                  {errors.price && (
                    <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prix original (€)
                  </label>
                  <input
                    {...register('originalPrice', { 
                      min: { value: 0, message: 'Le prix doit être positif' },
                      valueAsNumber: true
                    })}
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    placeholder="399.00"
                  />
                  {errors.originalPrice && (
                    <p className="mt-1 text-sm text-red-600">{errors.originalPrice.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durée estimée (heures)
                  </label>
                  <input
                    {...register('estimatedDuration', { 
                      min: { value: 0, message: 'La durée doit être positive' },
                      valueAsNumber: true
                    })}
                    type="number"
                    step="0.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    placeholder="10"
                  />
                  {errors.estimatedDuration && (
                    <p className="mt-1 text-sm text-red-600">{errors.estimatedDuration.message}</p>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register('isPublished')}
                    className="rounded border-gray-300 text-[#FFB639] shadow-sm focus:border-[#FFB639] focus:ring focus:ring-[#FFB639] focus:ring-opacity-50"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700">
                    Publier le cours
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut *
                  </label>
                  <select
                    {...register('status', { required: 'Le statut est requis' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  >
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                  {errors.status && (
                    <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image de couverture
                  </label>
                  <div 
                    {...getRootProps()} 
                    className={`border-2 ${isDragActive ? 'border-[#FFB639] bg-[#FFB639]/10' : 'border-dashed border-gray-300'} rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors`}
                  >
                    <input {...getInputProps()} />
                    {thumbnailPreview ? (
                      <div className="flex flex-col items-center">
                        <img 
                          src={thumbnailPreview.startsWith('blob:') || thumbnailPreview.startsWith('data:') ? 
                            thumbnailPreview : 
                            thumbnailPreview
                          }
                          alt="Thumbnail preview" 
                          className="h-40 object-cover rounded-lg mb-4" 
                        />
                        <p className="text-sm text-gray-600">
                          Cliquez ou glissez-déposez pour changer l'image
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="h-12 w-12 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">
                          Cliquez ou glissez-déposez une image ici
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PNG, JPG, GIF jusqu'à 5MB
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (séparés par des virgules)
                  </label>
                  <input
                    {...register('tags')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    placeholder="Ex: GPS, Analyse, Données"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prérequis (un par ligne)
                  </label>
                  <textarea
                    {...register('requirements')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    placeholder="Ex: Connaissance de base en informatique"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Objectifs d'apprentissage (un par ligne)
                  </label>
                  <textarea
                    {...register('objectives')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    placeholder="Ex: Maîtriser les concepts fondamentaux"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Public cible (un par ligne)
                  </label>
                  <textarea
                    {...register('targetAudience')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    placeholder="Ex: Professionnels du secteur agricole"
                  />
                </div>
              </div>
            </form>
          )}

          {activeTab === 'content' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Sections du cours</h3>
                <button
                  onClick={() => {
                    setEditingSection(null);
                    setShowSectionModal(true);
                  }}
                  className="flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une section
                </button>
              </div>

              {sections.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune section créée</h3>
                  <p className="mb-4">Commencez par créer votre première section de cours</p>
                  <button
                    onClick={() => {
                      setEditingSection(null);
                      setShowSectionModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer la première section
                  </button>
                </div>
              )}

              <div className="space-y-4">
                {sections.map((section, sectionIndex) => (
                  <div key={section._id || `new-section-${sectionIndex}`} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">{section.title}</h4>
                        {section.description && (
                          <p className="text-gray-600 mt-1">{section.description}</p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setEditingModule({ sectionId: section._id || `new-section-${sectionIndex}`, module: null });
                            setShowModuleModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-[#FFB639] transition-colors"
                          title="Ajouter un module"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => {
                            setEditingSection(section);
                            setShowSectionModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-[#FFB639] transition-colors"
                          title="Modifier la section"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => deleteSection(section._id, section.title)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title="Supprimer la section"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {section.modules.map((module, moduleIndex) => {
                        const Icon = getModuleIcon(module.type);
                        return (
                          <div key={module._id || `new-module-${sectionIndex}-${moduleIndex}`} className="flex items-center p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3 flex-1">
                              <Icon className={`h-5 w-5 ${getModuleColor(module.type)}`} />
                              <div>
                                <h5 className="font-medium text-gray-900">{module.title}</h5>
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                  <span>{moduleTypes.find(mt => mt.value === module.type)?.label}</span>
                                  {module.duration > 0 && <span>{module.duration} min</span>}
                                  {module.isRequired && <span className="text-[#FFB639]">Requis</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button 
                                onClick={() => {
                                  setEditingModule({ sectionId: section._id || `new-section-${sectionIndex}`, module });
                                  setShowModuleModal(true);
                                }}
                                className="p-1 text-gray-400 hover:text-[#FFB639] transition-colors"
                                title="Modifier le module"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => deleteModule(section._id || `new-section-${sectionIndex}`, module._id, module.title)}
                                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                title="Supprimer le module"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {section.modules.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p>Aucun module dans cette section</p>
                          <button
                            onClick={() => {
                              setEditingModule({ sectionId: section._id || `new-section-${sectionIndex}`, module: null });
                              setShowModuleModal(true);
                            }}
                            className="mt-2 text-[#FFB639] hover:text-[#e5a333]"
                          >
                            Ajouter le premier module
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Paramètres du cours</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('settings.allowDownload')}
                      className="rounded border-gray-300 text-[#FFB639] shadow-sm focus:border-[#FFB639] focus:ring focus:ring-[#FFB639] focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Permettre le téléchargement des ressources
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('settings.allowComments')}
                      className="rounded border-gray-300 text-[#FFB639] shadow-sm focus:border-[#FFB639] focus:ring focus:ring-[#FFB639] focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Permettre les commentaires
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('settings.sequentialAccess')}
                      className="rounded border-gray-300 text-[#FFB639] shadow-sm focus:border-[#FFB639] focus:ring focus:ring-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Accès séquentiel (ordre obligatoire)
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('certificate.enabled')}
                      className="rounded border-gray-300 text-[#FFB639] shadow-sm focus:border-[#FFB639] focus:ring focus:ring-[#FFB639] focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Certificat de fin de cours
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Limite de temps d'accès (jours)
                </label>
                <input
                  type="number"
                  min="0"
                  {...register('settings.timeLimit', { valueAsNumber: true })}
                  placeholder="0 = illimité"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                />
                <p className="mt-1 text-sm text-gray-500">
                  0 = accès illimité, sinon nombre de jours après inscription
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Score minimum pour obtenir le certificat (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  {...register('certificate.passingScore', { valueAsNumber: true })}
                  placeholder="70"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                />
              </div>
            </div>
          )}
        </div>

        {/* Section Modal */}
        {showSectionModal && (
          <SectionModal
            onClose={() => {
              setShowSectionModal(false);
              setEditingSection(null);
            }}
            onSave={editingSection ? updateSection : addSection}
            section={editingSection}
          />
        )}

        {/* Module Modal */}
        {showModuleModal && (
          <ModuleModal
            onClose={() => {
              setShowModuleModal(false);
              setEditingModule({ sectionId: '', module: null });
            }}
            onSave={(moduleData) => 
              editingModule.module 
                ? updateModule(editingModule.sectionId, moduleData) 
                : addModule(editingModule.sectionId, moduleData)
            }
            module={editingModule.module}
            moduleTypes={moduleTypes}
            uploadProgress={uploadProgress}
          />
        )}
      </div>
    </Layout>
  );
};

// Section Modal Component
const SectionModal: React.FC<{
  onClose: () => void;
  onSave: (data: { title: string; description: string }) => void;
  section?: Section | null;
}> = ({ onClose, onSave, section }) => {
  const [title, setTitle] = useState(section?.title || '');
  const [description, setDescription] = useState(section?.description || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (title.trim()) {
      onSave({ title: title.trim(), description: description.trim() });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {section ? 'Modifier la section' : 'Nouvelle section'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titre de la section *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
              placeholder="Ex: Introduction aux données GPS"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (optionnel)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
              placeholder="Décrivez le contenu de cette section..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
            >
              {section ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Module Modal Component
const ModuleModal: React.FC<{
  onClose: () => void;
  onSave: (data: Module) => void;
  module?: Module | null;
  moduleTypes: any[];
  sectionId?: string;
  uploadProgress?: number | null;
}> = ({ onClose, onSave, module, moduleTypes, uploadProgress }) => {
  const [formData, setFormData] = useState<Module>({
    title: module?.title || '',
    description: module?.description || '',
    type: module?.type || 'text',
    content: module?.type === 'text' ? module?.content : 
             module?.type === 'link' ? module?.externalUrl : '',
    duration: module?.duration || 0,
    isRequired: module?.isRequired ?? false,
    fileUrl: module?.fileUrl, // Keep existing fileUrl
    externalUrl: module?.externalUrl, // Keep existing externalUrl
  });
  const [file, setFile] = useState<File | undefined>(undefined);
  const [filePreview, setFilePreview] = useState<string | null>(null);

  useEffect(() => {
    if (module?.fileUrl && ['video', 'pdf', 'image', 'document'].includes(module.type)) {
      setFilePreview(`${API_BASE_URL}${module.fileUrl}`);
    } else {
      setFilePreview(null);
    }
  }, [module]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.title.trim()) {
      const submitData: Module = { 
        ...formData,
        file: file, // Attach the file object for submission
      };
      onSave(submitData);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result as string);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setFilePreview(null);
      }
    }
  };

  const needsFile = ['video', 'pdf', 'image', 'document'].includes(formData.type);
  const needsContent = ['text', 'link'].includes(formData.type);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {module ? 'Modifier le module' : 'Nouveau module'}
            </h3>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre du module *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                placeholder="Ex: Installation et configuration"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de module *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  type: e.target.value as any,
                  content: '', // Reset content when type changes
                  file: undefined, // Reset file
                  fileUrl: undefined, // Reset fileUrl
                  externalUrl: undefined // Reset externalUrl
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
              >
                {moduleTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                placeholder="Décrivez le contenu de ce module..."
              />
            </div>

            {needsFile && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fichier {!module?.fileUrl && !file && '*'}
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  accept={
                    formData.type === 'video' ? 'video/*' :
                    formData.type === 'pdf' ? '.pdf' :
                    formData.type === 'image' ? 'image/*' :
                    '.doc,.docx,.pdf,.ppt,.pptx'
                  }
                  required={!module?.fileUrl && !file && !module?.content}
                />
                {uploadProgress !== null && (
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                    <div 
                      className="bg-[#FFB639] h-2.5 rounded-full" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}
                {(filePreview || module?.fileUrl) && formData.type === 'image' && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-1">Aperçu :</p>
                    <img 
                      src={filePreview || `${API_BASE_URL}${module?.fileUrl}`} 
                      alt="Preview" 
                      className="h-40 object-contain rounded-lg border border-gray-200" 
                    />
                  </div>
                )}
                {(filePreview || module?.fileUrl) && formData.type !== 'image' && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">
                      Fichier actuel : {filePreview ? filePreview.split('/').pop() : module?.fileUrl?.split('/').pop()}
                    </p>
                  </div>
                )}
              </div>
            )}

            {needsContent && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.type === 'link' ? 'URL *' : 'Contenu *'}
                </label>
                {formData.type === 'text' ? (
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    placeholder="Saisissez le contenu textuel..."
                    required
                  />
                ) : (
                  <input
                    type="url"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                    placeholder="https://..."
                    required
                  />
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  placeholder="0"
                />
              </div>

              <div className="flex items-end">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isRequired}
                    onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                    className="rounded border-gray-300 text-[#FFB639] shadow-sm focus:border-[#FFB639] focus:ring focus:ring-[#FFB639] focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Module requis</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
               Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
            >
              {module ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseCreator;